import { useEffect, useState } from "react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [providers, setProviders] = useState([]);
  const [maintenance, setMaintenance] = useState(null);
  const [regEnabled, setRegEnabled] = useState(true);
  const [savingReg, setSavingReg] = useState(false);
  const [trialDays, setTrialDays] = useState(0);
  const [trialDraft, setTrialDraft] = useState(0);
  const [trialSaving, setTrialSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteForm, setNoteForm] = useState({ title: "", body: "", audience: "public", version_tag: "" });
  const [noteBusy, setNoteBusy] = useState(false);
  // Resend (lead notification email) settings
  const [resendCfg, setResendCfg] = useState(null);
  const [resendForm, setResendForm] = useState({ api_key: "", sender_email: "", recipients: "" });
  const [resendSaving, setResendSaving] = useState(false);
  const [resendTesting, setResendTesting] = useState(false);
  const [resendTestTo, setResendTestTo] = useState("");
  // Publishers (admin approval queue)
  const [publishers, setPublishers] = useState([]);
  const [pubFilter, setPubFilter] = useState("pending");
  const [pubBusy, setPubBusy] = useState({});

  const loadPublishers = async (status = pubFilter) => {
    try {
      const { data } = await api.get(`/admin/publishers${status ? `?status=${status}` : ""}`);
      setPublishers(data || []);
    } catch {
      toast.error("Failed to load publishers");
    }
  };

  const setPublisherStatus = async (userId, status, message = "") => {
    setPubBusy((b) => ({ ...b, [userId]: true }));
    try {
      await api.patch(`/admin/publishers/${userId}/status`, { status, message });
      await loadPublishers();
      toast.success(`Publisher marked ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setPubBusy((b) => ({ ...b, [userId]: false }));
    }
  };

  const load = async () => {
    try {
      const [s, u, b, l, sys, pp, mm, tt, nn, rs, pubs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/books"),
        api.get("/admin/leads"),
        api.get("/system/settings"),
        api.get("/publishing/providers"),
        api.get("/admin/maintenance/status"),
        api.get("/admin/trial/settings"),
        api.get("/release-notes"),
        api.get("/admin/notifications/resend"),
        api.get("/admin/publishers?status=pending"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setBooks(b.data);
      setLeads(l.data);
      setRegEnabled(!!sys.data.registration_enabled);
      setProviders(pp.data.providers || []);
      setMaintenance(mm.data);
      setTrialDays(tt.data.trial_days || 0);
      setTrialDraft(tt.data.trial_days || 0);
      setNotes(nn.data || []);
      setResendCfg(rs.data);
      setResendForm({
        api_key: "",
        sender_email: rs.data?.sender_source === "db" ? rs.data.sender_email : "",
        recipients: rs.data?.recipients_source === "db" ? rs.data.recipients : "",
      });
      setPublishers(pubs.data || []);
    } catch {
      toast.error("Failed to load admin data");
    }
  };

  const createNote = async (e) => {
    e.preventDefault();
    if (!noteForm.title.trim() || !noteForm.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setNoteBusy(true);
    try {
      const { data } = await api.post("/admin/release-notes", noteForm);
      setNotes([data, ...notes]);
      setNoteForm({ title: "", body: "", audience: "public", version_tag: "" });
      toast.success("Release note published");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setNoteBusy(false);
    }
  };

  const deleteNote = async (id) => {
    if (!confirm("Delete this release note?")) return;
    try {
      await api.delete(`/admin/release-notes/${id}`);
      setNotes(notes.filter((n) => n.note_id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  const saveTrialDays = async () => {
    setTrialSaving(true);
    try {
      const { data } = await api.patch("/admin/trial/settings", { trial_days: Number(trialDraft) });
      setTrialDays(data.trial_days);
      toast.success(`Trial set to ${data.trial_days} days for new signups`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setTrialSaving(false);
    }
  };

  const extendUserTrial = async (userId, days) => {
    try {
      const { data } = await api.patch(`/admin/users/${userId}/trial`, { extend_days: days });
      setUsers((rows) => rows.map((u) => u.user_id === userId ? { ...u, ...data } : u));
      toast.success(`Extended by ${days} days`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const unlimitUser = async (userId) => {
    try {
      const { data } = await api.patch(`/admin/users/${userId}/trial`, { trial_ends_at: null });
      setUsers((rows) => rows.map((u) => u.user_id === userId ? { ...u, ...data } : u));
      toast.success("Set to unlimited");
    } catch {
      toast.error("Failed");
    }
  };

  const toggleProvider = async (id, next) => {
    const current = providers.reduce((acc, p) => ({ ...acc, [p.id]: p.enabled }), {});
    current[id] = next;
    try {
      const { data } = await api.patch("/admin/publishing/providers", { providers: current });
      setProviders(data.providers);
      toast.success("Publishing providers updated");
    } catch {
      toast.error("Failed");
    }
  };

  const saveResend = async () => {
    setResendSaving(true);
    try {
      const payload = {};
      // Only send api_key if the admin typed a fresh one (empty string means "keep existing")
      if (resendForm.api_key.trim()) payload.api_key = resendForm.api_key.trim();
      payload.sender_email = resendForm.sender_email.trim();
      payload.recipients = resendForm.recipients.trim();
      const { data } = await api.patch("/admin/notifications/resend", payload);
      setResendCfg(data);
      setResendForm((f) => ({ ...f, api_key: "" })); // clear key input after save
      toast.success("Lead notification settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setResendSaving(false);
    }
  };

  const clearResendKey = async () => {
    if (!confirm("Remove the saved Resend API key? The integration will fall back to env-var configuration (if any).")) return;
    setResendSaving(true);
    try {
      const { data } = await api.patch("/admin/notifications/resend", { api_key: "" });
      setResendCfg(data);
      toast.success("Saved API key cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setResendSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setResendTesting(true);
    try {
      const body = resendTestTo.trim() ? { to: resendTestTo.trim() } : {};
      const { data } = await api.post("/admin/notifications/resend/test", body);
      toast.success(`Test email sent to ${(data.recipients || []).join(", ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test email failed");
    } finally {
      setResendTesting(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRegistration = async (next) => {
    setSavingReg(true);
    try {
      const { data } = await api.patch("/admin/system/settings", { registration_enabled: next });
      setRegEnabled(!!data.registration_enabled);
      toast.success(next ? "Public sign-up enabled" : "Public sign-up disabled");
    } catch {
      toast.error("Failed to update");
    } finally {
      setSavingReg(false);
    }
  };

  const setRole = async (userId, role) => {
    try {
      await api.post(`/admin/users/${userId}/role`, { role });
      toast.success("Role updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const deleteBook = async (bookId) => {
    if (!confirm("Delete this book permanently?")) return;
    try {
      await api.delete(`/admin/books/${bookId}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
      setLeads(leads.map((l) => (l.lead_id === leadId ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed");
    }
  };

  const leadCounts = leads.reduce(
    (acc, l) => {
      acc[l.status] = (acc[l.status] || 0) + 1;
      return acc;
    },
    { new: 0, contacted: 0, closed: 0 }
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="admin-page">
      <SiteNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14 space-y-14">
        <div>
          <div className="label-eyebrow mb-3">Bookforge HQ</div>
          <h1 className="font-serif text-5xl">Admin</h1>
        </div>

        {stats && (
          <section className="grid md:grid-cols-4 gap-6" data-testid="admin-stats">
            {[
              ["Users", stats.users],
              ["Books", stats.books],
              ["Chapters", stats.chapters],
              ["Public books", stats.public_books],
            ].map(([label, value]) => (
              <div key={label} className="p-6 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
                <div className="label-eyebrow mb-3">{label}</div>
                <div className="font-serif text-5xl" style={{ fontVariantNumeric: "tabular-nums", fontFamily: "Work Sans, sans-serif", fontWeight: 600 }}>{value}</div>
              </div>
            ))}
          </section>
        )}

        <section data-testid="admin-system-settings">
          <h2 className="font-serif text-3xl mb-4">System settings</h2>
          <div
            className="p-6 rounded-lg flex items-center justify-between"
            style={{ background: "white", border: "1px solid var(--bf-border)" }}
          >
            <div>
              <div className="font-serif text-2xl mb-1">Public sign-up</div>
              <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                When off, the Register page and Google sign-in stop creating new accounts. Existing users can still log in.
              </p>
              <p className="text-xs mt-2" style={{ color: regEnabled ? "var(--bf-secondary)" : "var(--bf-primary)" }} data-testid="admin-reg-status">
                Currently: <strong>{regEnabled ? "Enabled — anyone can sign up" : "Disabled — invitation only"}</strong>
              </p>
            </div>
            <Switch
              checked={regEnabled}
              disabled={savingReg}
              onCheckedChange={toggleRegistration}
              data-testid="admin-reg-toggle"
            />
          </div>
        </section>

        <section data-testid="admin-trial-section">
          <h2 className="font-serif text-3xl mb-4">Free trial</h2>
          <div className="p-5 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-xl mb-1">Free trial length for new authors</p>
              <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                Number of days a new author has full access after sign-up. After expiry the app is read-only — they can still view + export their books but cannot create, edit or use AI features. <strong>Existing users are not affected by this setting</strong> (their access remains unchanged unless you set a custom expiry below).
              </p>
              <p className="text-xs mt-2" style={{ color: trialDays === 0 ? "var(--bf-secondary)" : "var(--bf-primary)" }} data-testid="admin-trial-current">
                Currently: <strong>{trialDays === 0 ? "0 days — no trial (new signups get unlimited access)" : `${trialDays} days`}</strong>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={3650}
                value={trialDraft}
                onChange={(e) => setTrialDraft(e.target.value)}
                className="w-24"
                data-testid="admin-trial-input"
              />
              <span className="text-sm" style={{ color: "var(--bf-text-muted)" }}>days</span>
              <Button
                onClick={saveTrialDays}
                disabled={trialSaving || Number(trialDraft) === trialDays}
                className="text-white"
                style={{ background: "var(--bf-primary)" }}
                data-testid="admin-trial-save"
              >
                {trialSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </section>

        <section data-testid="admin-publishers-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-serif text-3xl">Publishers</h2>
              <p className="text-sm mt-1" style={{ color: "var(--bf-text-muted)" }}>
                Approve, reject, or request changes from publisher signups. Only approved publishers
                appear in the public directory and the Publish-tab dropdown.
              </p>
            </div>
            <div className="flex gap-1 text-xs">
              {["pending", "approved", "changes_requested", "rejected"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setPubFilter(s); loadPublishers(s); }}
                  className="px-3 py-1.5 rounded transition-colors"
                  style={{
                    background: pubFilter === s ? "var(--bf-primary)" : "white",
                    color: pubFilter === s ? "white" : "var(--bf-text)",
                    border: "1px solid var(--bf-border)",
                  }}
                  data-testid={`admin-pub-filter-${s}`}
                >
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          {publishers.length === 0 ? (
            <div className="p-8 text-center rounded-lg text-sm" style={{ background: "white", border: "1px dashed var(--bf-border)", color: "var(--bf-text-muted)" }} data-testid="admin-publishers-empty">
              No publishers in this state.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="admin-publishers-list">
              {publishers.map((row) => (
                <li
                  key={row.user.user_id}
                  className="p-4 rounded-lg"
                  style={{ background: "white", border: "1px solid var(--bf-border)" }}
                  data-testid={`admin-publisher-${row.user.user_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-xl">{row.profile.company_name || row.user.name}</p>
                      <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                        {row.user.email} · joined {new Date(row.user.created_at || Date.now()).toLocaleDateString()}
                      </p>
                      {row.profile.tagline && <p className="text-sm mt-1.5">{row.profile.tagline}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{ color: "var(--bf-text-muted)" }}>
                        {row.profile.base_fee != null && <span>{row.profile.currency} {Number(row.profile.base_fee).toLocaleString()}</span>}
                        {row.profile.turnaround && <span>· {row.profile.turnaround}</span>}
                        {(row.profile.services || []).length > 0 && <span>· {(row.profile.services || []).slice(0, 3).join(", ")}{(row.profile.services || []).length > 3 ? "…" : ""}</span>}
                      </div>
                      {row.profile.review_message && (
                        <p className="text-xs mt-2 italic" style={{ color: "var(--bf-text-muted)" }}>
                          Last admin note: &ldquo;{row.profile.review_message}&rdquo;
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <span className="text-[10px] px-2 py-0.5 rounded-full self-end" style={{ background: "var(--bf-surface)" }}>
                        {row.profile.approval_status}
                      </span>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={pubBusy[row.user.user_id]}
                          onClick={() => {
                            const m = prompt("What needs changing? (Optional message to the publisher)") || "";
                            setPublisherStatus(row.user.user_id, "changes_requested", m);
                          }}
                          data-testid={`admin-pub-request-${row.user.user_id}`}
                        >
                          Request changes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={pubBusy[row.user.user_id]}
                          onClick={() => {
                            const m = prompt("Reason for rejection? (Optional)") || "";
                            setPublisherStatus(row.user.user_id, "rejected", m);
                          }}
                          data-testid={`admin-pub-reject-${row.user.user_id}`}
                        >
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          className="text-white"
                          style={{ background: "var(--bf-primary)" }}
                          disabled={pubBusy[row.user.user_id]}
                          onClick={() => setPublisherStatus(row.user.user_id, "approved", "Welcome aboard!")}
                          data-testid={`admin-pub-approve-${row.user.user_id}`}
                        >
                          Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="admin-resend-section">
          <h2 className="font-serif text-3xl mb-4">Lead notification emails</h2>
          <p className="text-sm mb-4" style={{ color: "var(--bf-text-muted)" }}>
            When a visitor submits an &quot;I&apos;m interested&quot; lead, Bookforge can email your admin team via{" "}
            <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">Resend</a>.
            Save your API key, verified sender, and recipient list here — no server restart required.
          </p>
          <div className="p-5 rounded-lg space-y-4" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
            {/* Current status pill */}
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="admin-resend-status">
              <span
                className="px-2 py-1 rounded-full"
                style={{
                  background: resendCfg?.api_key_present ? "#dcfce7" : "#fee2e2",
                  color: resendCfg?.api_key_present ? "#166534" : "#991b1b",
                }}
              >
                {resendCfg?.api_key_present ? "Configured" : "Not configured"}
              </span>
              {resendCfg?.api_key_present && (
                <span style={{ color: "var(--bf-text-muted)" }}>
                  Key: <code className="px-1.5 py-0.5 rounded" style={{ background: "var(--bf-surface)" }}>{resendCfg.api_key_masked}</code>{" "}
                  <span className="opacity-70">(source: {resendCfg.api_key_source})</span>
                </span>
              )}
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5" htmlFor="resend-api-key">Resend API key</label>
              <div className="flex gap-2">
                <Input
                  id="resend-api-key"
                  type="password"
                  placeholder={resendCfg?.api_key_present ? "Leave blank to keep current key" : "re_xxxxxxxxxxxx"}
                  value={resendForm.api_key}
                  onChange={(e) => setResendForm({ ...resendForm, api_key: e.target.value })}
                  data-testid="admin-resend-api-key"
                  autoComplete="off"
                />
                {resendCfg?.api_key_present && resendCfg?.api_key_source === "db" && (
                  <Button
                    variant="outline"
                    onClick={clearResendKey}
                    disabled={resendSaving}
                    data-testid="admin-resend-clear-key"
                  >
                    Remove key
                  </Button>
                )}
              </div>
              <p className="text-xs mt-1.5" style={{ color: "var(--bf-text-muted)" }}>
                Get one from your{" "}
                <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">Resend dashboard → API Keys</a>.
                Stored encrypted at rest; only the last 4 characters are ever returned.
              </p>
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5" htmlFor="resend-sender">Verified sender email</label>
              <Input
                id="resend-sender"
                type="email"
                placeholder="notifications@yourdomain.com"
                value={resendForm.sender_email}
                onChange={(e) => setResendForm({ ...resendForm, sender_email: e.target.value })}
                data-testid="admin-resend-sender"
              />
              <p className="text-xs mt-1.5" style={{ color: "var(--bf-text-muted)" }}>
                Must be verified in your Resend account. While in Resend test-mode, you can use{" "}
                <code>onboarding@resend.dev</code>.
              </p>
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5" htmlFor="resend-recipients">Admin recipients (comma-separated)</label>
              <Input
                id="resend-recipients"
                placeholder="you@example.com, team@example.com"
                value={resendForm.recipients}
                onChange={(e) => setResendForm({ ...resendForm, recipients: e.target.value })}
                data-testid="admin-resend-recipients"
              />
              <p className="text-xs mt-1.5" style={{ color: "var(--bf-text-muted)" }}>
                These addresses receive the lead notification. In Resend test-mode, only addresses verified on your Resend account will deliver.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: "var(--bf-border)" }}>
              <Button
                onClick={saveResend}
                disabled={resendSaving}
                className="text-white"
                style={{ background: "var(--bf-primary)" }}
                data-testid="admin-resend-save"
              >
                {resendSaving ? "Saving…" : "Save settings"}
              </Button>
              <span className="text-sm" style={{ color: "var(--bf-text-muted)" }}>— or —</span>
              <Input
                placeholder="Send a test to… (defaults to saved recipients)"
                value={resendTestTo}
                onChange={(e) => setResendTestTo(e.target.value)}
                className="max-w-xs"
                data-testid="admin-resend-test-to"
              />
              <Button
                variant="outline"
                onClick={sendTestEmail}
                disabled={resendTesting || !resendCfg?.api_key_present}
                data-testid="admin-resend-test-btn"
              >
                {resendTesting ? "Sending…" : "Send test email"}
              </Button>
            </div>
          </div>
        </section>

        <section data-testid="admin-publishing-section">
          <h2 className="font-serif text-3xl mb-4">Publishing platforms</h2>
          <p className="text-sm mb-4" style={{ color: "var(--bf-text-muted)" }}>
            Toggle which providers appear on the author's "Publish" tab. The Self-publish bundle works fully in-app; the other providers open their external dashboards with author metadata copied to clipboard.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {providers.map((p) => (
              <div
                key={p.id}
                className="p-5 rounded-lg flex items-start gap-3"
                style={{ background: "white", border: "1px solid var(--bf-border)" }}
                data-testid={`admin-provider-${p.id}`}
              >
                <Switch
                  checked={p.enabled}
                  onCheckedChange={(next) => toggleProvider(p.id, next)}
                  data-testid={`admin-provider-toggle-${p.id}`}
                />
                <div>
                  <p className="font-serif text-lg leading-tight">{p.label}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>{p.tagline}</p>
                  {p.deep_link && (
                    <p className="text-xs mt-1 break-all"><a href={p.deep_link} target="_blank" rel="noreferrer" className="underline">{p.deep_link}</a></p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>


        <section data-testid="admin-release-notes-section">
          <h2 className="font-serif text-3xl mb-4">Release notes (What's new)</h2>
          <p className="text-sm mb-4" style={{ color: "var(--bf-text-muted)" }}>
            Public notes are visible to every logged-in author via the "What's new" bell. Admin-only notes appear only to admins.
          </p>
          <form onSubmit={createNote} className="p-5 rounded-lg mb-5 grid gap-3" style={{ background: "white", border: "1px solid var(--bf-border)" }} data-testid="admin-release-notes-form">
            <div className="grid sm:grid-cols-[1fr_140px_140px] gap-2">
              <Input
                placeholder="Title (e.g. 'AI outline planner is live')"
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                data-testid="release-note-title"
              />
              <Input
                placeholder="v0.13"
                value={noteForm.version_tag}
                onChange={(e) => setNoteForm({ ...noteForm, version_tag: e.target.value })}
                data-testid="release-note-version"
              />
              <select
                value={noteForm.audience}
                onChange={(e) => setNoteForm({ ...noteForm, audience: e.target.value })}
                className="border rounded px-2 text-sm"
                style={{ borderColor: "var(--bf-border)" }}
                data-testid="release-note-audience"
              >
                <option value="public">Public (all authors)</option>
                <option value="admin">Admin only</option>
              </select>
            </div>
            <textarea
              rows={3}
              placeholder="What's new in this release?"
              value={noteForm.body}
              onChange={(e) => setNoteForm({ ...noteForm, body: e.target.value })}
              className="border rounded px-3 py-2 text-sm"
              style={{ borderColor: "var(--bf-border)" }}
              data-testid="release-note-body"
            />
            <div>
              <Button type="submit" disabled={noteBusy} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="release-note-publish-btn">
                {noteBusy ? "Publishing…" : "Publish"}
              </Button>
            </div>
          </form>
          <ul className="space-y-2" data-testid="admin-release-notes-list">
            {notes.length === 0 && (
              <li className="text-sm py-3" style={{ color: "var(--bf-text-muted)" }} data-testid="admin-release-notes-empty">
                No release notes yet.
              </li>
            )}
            {notes.map((n) => (
              <li
                key={n.note_id}
                className="p-3 rounded flex items-start justify-between gap-3"
                style={{ background: "white", border: "1px solid var(--bf-border)" }}
                data-testid={`admin-release-note-${n.note_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-sm">{n.title}</span>
                    {n.version_tag && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--bf-surface)" }}>{n.version_tag}</span>}
                    <span
                      className="text-xs px-2 py-0.5 rounded uppercase font-medium"
                      style={n.audience === "admin"
                        ? { background: "#FEF3C7", color: "#92400E" }
                        : { background: "#DBEAFE", color: "#1E40AF" }}
                      data-testid={`admin-release-note-audience-${n.note_id}`}
                    >
                      {n.audience}
                    </span>
                  </div>
                  <p className="text-xs whitespace-pre-line" style={{ color: "var(--bf-text-muted)" }}>{n.body}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteNote(n.note_id)} data-testid={`admin-release-note-delete-${n.note_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        </section>

        {maintenance && (
          <section data-testid="admin-maintenance-section">
            <h2 className="font-serif text-3xl mb-4">Maintenance</h2>
            <div className="p-5 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
              <p className="font-serif text-xl mb-1">Orphan file sweep</p>
              <p className="text-xs mb-3" style={{ color: "var(--bf-text-muted)" }}>
                Runs every {Math.round(maintenance.sweep.interval_seconds / 3600)}h. Retention: {maintenance.sweep.retention_days} days.
              </p>
              <p className="text-sm" data-testid="admin-sweep-last-run">
                Last run: <strong>{maintenance.sweep.last_run_at ? new Date(maintenance.sweep.last_run_at).toLocaleString() : "never"}</strong> · purged {maintenance.sweep.last_purged} row(s)
              </p>
            </div>
          </section>
        )}

        <section data-testid="admin-leads-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <h2 className="font-serif text-3xl">Bookforge interest leads</h2>
            <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="admin-leads-counts">
              <strong>{leadCounts.new}</strong> new ·{" "}
              <strong>{leadCounts.contacted}</strong> contacted ·{" "}
              <strong>{leadCounts.closed}</strong> closed
            </p>
          </div>
          <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid var(--bf-border)", background: "white" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bf-surface)" }}>
                <tr className="text-left">
                  <th className="p-3">Name</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Note</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">When</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="admin-leads-empty">
                      No leads yet — once visitors click "I'm interested in Bookforge" they'll appear here.
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr key={l.lead_id} className="border-t align-top" style={{ borderColor: "var(--bf-border)" }} data-testid={`admin-lead-${l.lead_id}`}>
                    <td className="p-3">{l.name || <span style={{ color: "var(--bf-text-muted)" }}>—</span>}</td>
                    <td className="p-3 text-xs"><a href={`mailto:${l.email}`} className="underline">{l.email}</a></td>
                    <td className="p-3 text-xs">{l.phone || <span style={{ color: "var(--bf-text-muted)" }}>—</span>}</td>
                    <td className="p-3 text-xs max-w-xs whitespace-pre-line">{l.note || <span style={{ color: "var(--bf-text-muted)" }}>—</span>}</td>
                    <td className="p-3">
                      <span
                        className="text-xs px-2 py-1 rounded uppercase tracking-wider"
                        style={{
                          background: l.status === "new" ? "var(--bf-accent)" : l.status === "contacted" ? "var(--bf-primary)" : "var(--bf-surface)",
                          color: l.status === "closed" ? "var(--bf-text-muted)" : "white",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}
                        data-testid={`admin-lead-status-${l.lead_id}`}
                      >
                        {l.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {l.status !== "contacted" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateLeadStatus(l.lead_id, "contacted")}
                          data-testid={`admin-lead-contacted-${l.lead_id}`}
                          className="mr-1"
                        >
                          Mark contacted
                        </Button>
                      )}
                      {l.status !== "closed" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateLeadStatus(l.lead_id, "closed")}
                          data-testid={`admin-lead-closed-${l.lead_id}`}
                        >
                          Close
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-users">
          <h2 className="font-serif text-3xl mb-4">Users</h2>
          <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid var(--bf-border)", background: "white" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bf-surface)" }}>
                <tr className="text-left">
                  <th className="p-3">Name</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Role</th>
                  <th className="p-3">Slug</th>
                  <th className="p-3">Trial</th>
                  <th className="p-3">Created</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const status = u.trial_status || "unlimited";
                  const statusStyle = status === "expired"
                    ? { background: "#FEE2E2", color: "#991B1B" }
                    : status === "active"
                    ? { background: "#FEF3C7", color: "#92400E" }
                    : { background: "var(--bf-surface)", color: "var(--bf-text-muted)" };
                  return (
                    <tr key={u.user_id} className="border-t align-top" style={{ borderColor: "var(--bf-border)" }} data-testid={`admin-user-${u.user_id}`}>
                      <td className="p-3">{u.name}</td>
                      <td className="p-3 text-xs">{u.email}</td>
                      <td className="p-3"><span className="text-xs px-2 py-1 rounded" style={{ background: "var(--bf-surface)" }}>{u.role}</span></td>
                      <td className="p-3 text-xs">{u.slug}</td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        <span className="px-2 py-1 rounded font-medium uppercase" style={{ ...statusStyle, fontSize: "0.65rem" }} data-testid={`admin-user-trial-status-${u.user_id}`}>
                          {status}
                        </span>
                        {u.trial_ends_at && (
                          <span className="ml-2" style={{ color: "var(--bf-text-muted)" }}>
                            {status === "active" ? `${u.trial_days_remaining}d left` : `ended ${u.trial_ends_at.slice(0, 10)}`}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {u.role !== "admin" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => extendUserTrial(u.user_id, 30)} className="mr-1" data-testid={`admin-trial-extend-${u.user_id}`}>
                              +30d
                            </Button>
                            {u.trial_ends_at && (
                              <Button size="sm" variant="ghost" onClick={() => unlimitUser(u.user_id)} className="mr-1" data-testid={`admin-trial-unlimit-${u.user_id}`}>
                                ∞
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setRole(u.user_id, u.role === "admin" ? "author" : "admin")}
                          data-testid={`admin-toggle-role-${u.user_id}`}
                        >
                          Make {u.role === "admin" ? "author" : "admin"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-books-section">
          <h2 className="font-serif text-3xl mb-4">Books</h2>
          <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid var(--bf-border)", background: "white" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bf-surface)" }}>
                <tr className="text-left">
                  <th className="p-3">Title</th>
                  <th className="p-3">Owner</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Public</th>
                  <th className="p-3">Slug</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {books.map((b) => (
                  <tr key={b.book_id} className="border-t" style={{ borderColor: "var(--bf-border)" }} data-testid={`admin-book-${b.book_id}`}>
                    <td className="p-3">{b.title}</td>
                    <td className="p-3 text-xs">{b.owner_id}</td>
                    <td className="p-3">{b.status}</td>
                    <td className="p-3">{b.is_public ? "Yes" : "No"}</td>
                    <td className="p-3 text-xs">{b.slug}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="destructive" onClick={() => deleteBook(b.book_id)} data-testid={`admin-delete-book-${b.book_id}`}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
